import { useMemo } from 'react'
import { translateLabels, useTranslation, useWorkflowFeature, useWorkflowForCanvassFeature } from '@front/model'
import { type PageRoute } from '../navigation'

export type SettingsRouteFlags = {
  isWorkflowEnabled: boolean
  isWorkflowLoading: boolean
  isWorkflowForCanvassEnabled: boolean
  isWorkflowForCanvassLoading: boolean
}

export const buildSettingsRoutes = ({
  isWorkflowEnabled,
  isWorkflowLoading,
  isWorkflowForCanvassEnabled,
  isWorkflowForCanvassLoading,
}: SettingsRouteFlags): PageRoute[] => {
  const legacyCanvassHidden = isWorkflowForCanvassEnabled || isWorkflowForCanvassLoading
  return [
    {
      path: '/settings/company',
      label: 'General',
      icon: 'business',
      scope: 'admin',
    },
    {
      path: '/settings/configuration',
      label: 'Configuration',
      icon: 'settings',
    },
    {
      path: '/settings/workflow',
      label: 'Workflows',
      icon: 'workflow',
      hidden: !isWorkflowEnabled || isWorkflowLoading,
    },
    {
      path: '/settings/disposition',
      label: 'Dispositions',
      icon: 'location_on',
      scope: 'manager',
      hidden: legacyCanvassHidden,
    },
    {
      path: '/settings/status',
      label: 'Account Stages',
      icon: 'badge',
      scope: 'admin',
      hidden: legacyCanvassHidden,
    },
    { path: '/settings/role', label: 'Roles', icon: 'account_circle', scope: 'role:manage' },
    {
      path: '/settings/outcome',
      label: 'Closer Outcomes',
      icon: 'event',
      scope: 'admin',
      hidden: legacyCanvassHidden,
    },
    {
      path: '/settings/scorecard',
      label: 'Scorecards',
      icon: 'assignment_turned_in',
      scope: 'rubric:manage',
    },
    {
      path: '/settings/tile',
      label: 'Customer Map',
      icon: 'map',
      scope: 'admin',
    },
    { path: '/settings/permissions', label: 'Permissions', icon: 'shield' },
    {
      path: '/settings/customField',
      label: 'Custom Fields',
      icon: 'tune',
      scope: 'customField:save',
    },
    {
      path: '/settings/ratios',
      label: 'Custom Stats',
      icon: 'calculate',
      scope: 'ratio:save',
    },
    { path: '/settings/links', label: 'Links', icon: 'link', scope: 'externalLink:manage' },
    {
      path: '/settings/myCard',
      label: 'MyCard',
      icon: 'badge',
      scope: 'companyCard:save',
    },
    {
      path: '/settings/communicationPreferences',
      label: 'Notifications',
      icon: 'notifications',
    },
  ]
}

export function useSettingsRoutes(): PageRoute[] {
  const { t } = useTranslation()
  const { isEnabled: isWorkflowEnabled, isLoading: isWorkflowLoading } = useWorkflowFeature()
  const { isEnabled: isWorkflowForCanvassEnabled, isLoading: isWorkflowForCanvassLoading } =
    useWorkflowForCanvassFeature()

  const routes = useMemo(
    () =>
      buildSettingsRoutes({
        isWorkflowEnabled,
        isWorkflowLoading,
        isWorkflowForCanvassEnabled,
        isWorkflowForCanvassLoading,
      }),
    [isWorkflowEnabled, isWorkflowLoading, isWorkflowForCanvassEnabled, isWorkflowForCanvassLoading]
  )

  return translateLabels(routes, t)
}
